import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, Building2 } from "lucide-react";

interface ContractorProfile {
  id: string;
  user_id: string;
  company_name: string;
  contact_name: string;
  phone: string;
  approved: boolean;
  created_at: string;
}

const AdminContractorsTab = () => {
  const [contractors, setContractors] = useState<ContractorProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("contractor_profiles")
      .select("*")
      .order("created_at", { ascending: false });
    if (data) setContractors(data as ContractorProfile[]);
    setLoading(false);
  };

  const handleApprove = async (id: string) => {
    const { error } = await supabase
      .from("contractor_profiles")
      .update({ approved: true })
      .eq("id", id);
    if (error) {
      toast.error("Failed to approve");
    } else {
      toast.success("Contractor approved");
      fetchContractors();
    }
  };

  const handleReject = async (id: string) => {
    if (!confirm("Revoke this contractor's access?")) return;
    const { error } = await supabase
      .from("contractor_profiles")
      .update({ approved: false })
      .eq("id", id);
    if (error) {
      toast.error("Failed to update");
    } else {
      toast.success("Access revoked");
      fetchContractors();
    }
  };

  if (loading) {
    return <p className="text-muted-foreground text-sm">Loading contractors...</p>;
  }

  const pending = contractors.filter((c) => !c.approved);
  const approved = contractors.filter((c) => c.approved);

  return (
    <div className="space-y-8">
      {/* Pending */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-yellow-500 rounded-full" />
          Pending Approval ({pending.length})
        </h3>
        {pending.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending requests.</p>
        ) : (
          <div className="space-y-2">
            {pending.map((c) => (
              <div key={c.id} className="border border-border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground break-words">{c.company_name}</p>
                  <p className="text-sm text-muted-foreground break-words">
                    {c.contact_name && `${c.contact_name} · `}
                    {c.phone && `${c.phone} · `}
                    Applied {new Date(c.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" onClick={() => handleApprove(c.id)}>
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approved */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <span className="w-2 h-2 bg-green-500 rounded-full" />
          Approved ({approved.length})
        </h3>
        {approved.length === 0 ? (
          <p className="text-sm text-muted-foreground">No approved contractors yet.</p>
        ) : (
          <div className="space-y-2">
            {approved.map((c) => (
              <div key={c.id} className="border border-border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground break-words">{c.company_name}</p>
                  <p className="text-sm text-muted-foreground break-words">
                    {c.contact_name && `${c.contact_name} · `}
                    {c.phone}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleReject(c.id)} className="shrink-0 self-start sm:self-auto">
                  <X className="w-4 h-4 mr-1" /> Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContractorsTab;
